import { supabase } from '../../../lib/supabase';

/**
 * Persist document metadata to Supabase
 * Stores title, description, category, and file reference
 */
export const persistDocumentMetadata = async (userId, sectionId, file, metadata) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    const filePath = `portfolios/${userId}/${sectionId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('portfolio-uploads')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Failed to upload file: ${uploadError.message}`);
    }

    // Get signed URL (valid for 1 year)
    const { data: signedData, error: signedError } = await supabase.storage
      .from('portfolio-uploads')
      .createSignedUrl(filePath, 31536000); // 1 year in seconds

    if (signedError) {
      throw new Error(`Failed to create signed URL: ${signedError.message}`);
    }

    // Store metadata in database
    const documentRecord = {
      user_id: userId,
      category: sectionId,
      title: metadata.title,
      description: metadata.description,
      file_name: file.name,
      file_path: filePath,
      file_url: signedData.signedUrl,
      file_type: file.type,
      file_size: file.size,
      uploaded_at: new Date().toISOString(),
    };

    const { data, error: dbError } = await supabase
      .from('portfolio_documents')
      .insert([documentRecord])
      .select();

    if (dbError) {
      throw new Error(`Failed to save document metadata: ${dbError.message}`);
    }

    return {
      id: data?.[0]?.id || Date.now(),
      ...documentRecord,
      uploadedAt: Date.now(),
      url: signedData.signedUrl,
      alt: `${metadata.title} - ${file.name}`,
    };
  } catch (err) {
    console.error('Error persisting document metadata:', err);
    throw err;
  }
};

/**
 * Fetch all documents for a user
 */
export const fetchUserDocuments = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('portfolio_documents')
      .select('*')
      .eq('user_id', userId)
      .order('uploaded_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch documents: ${error.message}`);
    }

    // Group by category
    const grouped = {};
    data?.forEach((doc) => {
      if (!grouped[doc.category]) {
        grouped[doc.category] = [];
      }
      grouped[doc.category].push({
        id: doc.id,
        name: doc.file_name,
        title: doc.title,
        description: doc.description,
        type: doc.file_type,
        size: doc.file_size,
        url: doc.file_url,
        alt: `${doc.title} - ${doc.file_name}`,
        uploadedAt: new Date(doc.uploaded_at).getTime(),
      });
    });

    return grouped;
  } catch (err) {
    console.error('Error fetching user documents:', err);
    throw err;
  }
};

/**
 * Delete a document from Supabase
 */
export const deleteDocument = async (documentId, filePath) => {
  try {
    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('portfolio-uploads')
      .remove([filePath]);

    if (storageError) {
      console.warn('Storage deletion warning:', storageError);
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('portfolio_documents')
      .delete()
      .eq('id', documentId);

    if (dbError) {
      throw new Error(`Failed to delete document: ${dbError.message}`);
    }

    return true;
  } catch (err) {
    console.error('Error deleting document:', err);
    throw err;
  }
};

/**
 * Update document metadata
 */
export const updateDocumentMetadata = async (documentId, updates) => {
  try {
    const { data, error } = await supabase
      .from('portfolio_documents')
      .update({
        title: updates.title,
        description: updates.description,
      })
      .eq('id', documentId)
      .select();

    if (error) {
      throw new Error(`Failed to update document: ${error.message}`);
    }

    return data?.[0];
  } catch (err) {
    console.error('Error updating document:', err);
    throw err;
  }
};
/**
 * Save personal information to database
 */
export const savePersonalInfo = async (userId, personalInfo) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('personal_info')
      .upsert({
        user_id: userId,
        first_name: personalInfo.firstName,
        last_name: personalInfo.lastName,
        email: personalInfo.email,
        date_of_birth: personalInfo.dateOfBirth,
        phone: personalInfo.phoneNumber || personalInfo.phone || null,
        grade: personalInfo.grade,
        country: personalInfo.country,
        city: personalInfo.city,
        school: personalInfo.school,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      })
      .select();

    if (error) {
      throw new Error(`Failed to save personal info: ${error.message}`);
    }

    return data?.[0];
  } catch (err) {
    console.error('Error saving personal info:', err);
    throw err;
  }
};

/**
 * Fetch personal information from database
 */
export const fetchPersonalInfo = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('personal_info')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = not found
      throw new Error(`Failed to fetch personal info: ${error.message}`);
    }

    if (data) {
      return {
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        dateOfBirth: data.date_of_birth,
        phoneNumber: data.phone || null,
        grade: data.grade,
        country: data.country,
        city: data.city,
        school: data.school,
      };
    }

    return null;
  } catch (err) {
    console.error('Error fetching personal info:', err);
    return null;
  }
};

/**
 * Save onboarding results (AI-generated recommendations) to database
 */
export const saveResults = async (userId, selectedPath, responses, aiResults) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('onboarding_results')
      .upsert({
        user_id: userId,
        selected_path: selectedPath,
        responses: responses,
        ai_recommendations: aiResults,
        generated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      })
      .select();

    if (error) {
      throw new Error(`Failed to save results: ${error.message}`);
    }

    return data?.[0];
  } catch (err) {
    console.error('Error saving results:', err);
    throw err;
  }
};

/**
 * Fetch onboarding results (AI-generated recommendations) from database
 */
export const fetchResults = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('onboarding_results')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    // Treat 406 Not Acceptable (no row for single) as not-found
    if (error && error.code !== 'PGRST116' && error.status !== 406) {
      throw new Error(`Failed to fetch results: ${error.message}`);
    }

    if (data) {
      return {
        selectedPath: data.selected_path,
        responses: data.responses,
        aiResults: data.ai_recommendations,
        generatedAt: data.generated_at,
      };
    }

    return null;
  } catch (err) {
    console.error('Error fetching results:', err);
    return null;
  }
};

/**
 * Delete onboarding results
 */
export const deleteResults = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { error } = await supabase
      .from('onboarding_results')
      .delete()
      .eq('user_id', userId);

    if (error) {
      throw new Error(`Failed to delete results: ${error.message}`);
    }

    return true;
  } catch (err) {
    console.error('Error deleting results:', err);
    throw err;
  }
};